import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { ChevronDown, ChevronUp, Mail01, Paperclip, AlertHexagon } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import type { Container } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { formatRelativeTime } from '@/utils/date-formatters';
import { ContainerTimeline } from './container-timeline';

interface ContainerListItemProps {
    container: Container;
    onSelect?: (containerId: string) => void;
    showComments?: boolean;
    showAttachments?: boolean;
}

export const ContainerListItem = ({
    container,
    onSelect,
    showComments = true,
    showAttachments = true
}: ContainerListItemProps) => {
    const { translateContainerStatus, translateRiskLevel } = useI18n();
    const { t } = useTranslation('pages:containers');
    const [isExpanded, setIsExpanded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft': return 'gray';
            case 'Booked': return 'brand';
            case 'In Transit': return 'brand';
            case 'Arrived': return 'warning';
            case 'Delivered': return 'success';
            case 'On Hold': return 'warning';
            case 'Cancelled': return 'error';
            default: return 'gray';
        }
    };

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    // Email and document counts from API
    const emailCount = container.emailCount || 0;
    const attachmentsCount = container.documentCount || 0;

    // Calculate total issue count from documentStatusOverall and exogenStatusOverall
    const issueCount = useMemo(() => {
        let count = 0;
        // Parse document status (e.g., "2 docs need attention" -> 2)
        if (container.documentStatusOverall && container.documentStatusOverall !== 'All docs are OK') {
            const docMatch = container.documentStatusOverall.match(/^(\d+)/);
            if (docMatch) count += parseInt(docMatch[1], 10);
        }
        // Parse exogen status (e.g., "3 general problems" -> 3)
        if (container.exogenStatusOverall && container.exogenStatusOverall !== 'All systems OK') {
            const issueMatch = container.exogenStatusOverall.match(/^(\d+)/);
            if (issueMatch) count += parseInt(issueMatch[1], 10);
        }
        return count;
    }, [container.documentStatusOverall, container.exogenStatusOverall]);

    // Estrai waypoints per la timeline
    const waypoints = container.waypoints || [];


    return (
        <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
            {/* Main Content - Background Bianco */}
            <div
                className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                onClick={() => onSelect?.(container.id)}
            >
                <div className="flex items-start justify-between gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                        {/* Header: Container Number + Status */}
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-lg font-semibold text-primary">{container.containerNumber}</h4>
                            <Badge color={getStatusColor(container.status)} size="sm">
                                {translateContainerStatus(container.status)}
                            </Badge>
                        </div>
                    </div>

                    {/* Right Section: Comments, Attachments, Timestamp, Toggle */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Emails, Attachments & Issues */}
                        <div className="flex items-center gap-3 text-sm text-tertiary">
                            {showComments && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Navigate to activity feed filtered by email category and container number
                                        const params = new URLSearchParams();
                                        params.set('category', 'email');
                                        params.set('container', container.containerNumber || '');
                                        navigate(`/shipper/activity?${params.toString()}`, { state: navigationStateFrom(location) });
                                    }}
                                    className="flex items-center gap-1 hover:text-brand-secondary transition-colors"
                                    title={t("list.expandedDetails.viewEmails")}
                                >
                                    <Mail01 className="size-4" />
                                    <span>{emailCount}</span>
                                </button>
                            )}
                            {showAttachments && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Navigate to container documents tab
                                        navigate(`/shipper/containers/${container.id}?view=list&tab=documents`, { state: navigationStateFrom(location) });
                                    }}
                                    className="flex items-center gap-1 hover:text-brand-secondary transition-colors"
                                    title={t("list.expandedDetails.viewDocuments")}
                                >
                                    <Paperclip className="size-4" />
                                    <span>{attachmentsCount}</span>
                                </button>
                            )}
                            <div className={`flex items-center gap-1 ${issueCount > 0 ? 'text-warning-600' : ''}`}>
                                <AlertHexagon className="size-4" />
                                <span>{issueCount}</span>
                            </div>
                        </div>

                        {/* Timestamp */}
                        <div className="text-xs text-tertiary">
                            {formatRelativeTime(container.updatedAt)}
                        </div>

                        {/* Toggle Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            className="p-1 hover:bg-secondary rounded transition-colors"
                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                            {isExpanded ? (
                                <ChevronUp className="size-5 text-tertiary" />
                            ) : (
                                <ChevronDown className="size-5 text-tertiary" />
                            )}
                        </button>
                    </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                        {/* Shipping Company */}
                        {container.shippingCompany && (
                            <div className="text-sm text-primary font-medium mb-3">
                                {container.shippingCompany}
                            </div>
                        )}

                        {/* Timeline - Visualizzazione stato vessel */}
                        <ContainerTimeline
                            container={container}
                            waypoints={waypoints}
                        />

                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                {translateRiskLevel(container.riskLevel)}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Details - Background Grigio */}
            {isExpanded && (
                <div className="bg-secondary p-4 border-t border-secondary_alt">
                    {/* Header con trigger per chiudere - come nell'immagine */}
                    <div className="flex items-center justify-end mb-4">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(false);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-tertiary hover:text-primary hover:bg-secondary_alt rounded transition-colors"
                            aria-label="Collapse details"
                        >
                            <ChevronUp className="size-4" />
                            <span>{t("list.expandedDetails.hideDetails")}</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        {/* Booking & References */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.bookingNumber")}:</span>
                                <span className="text-primary ml-2">{container.bookingNumber || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.poNumber")}:</span>
                                <span className="text-primary ml-2">{container.purchaseOrderNumber || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.vessel")}:</span>
                                <span className="text-primary ml-2">{container.vesselName || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.voyage")}:</span>
                                <span className="text-primary ml-2">{container.voyageNumber || '-'}</span>
                            </div>
                        </div>

                        {/* Receiver Info */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.consignee")}:</span>
                                <span className="text-primary ml-2">{container.consigneeName || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.incoterms")}:</span>
                                <span className="text-primary ml-2">{container.incoterms || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.transportMode")}:</span>
                                <span className="text-primary ml-2">{container.transportMode || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.cargoType")}:</span>
                                <span className="text-primary ml-2">{container.cargoType || '-'}</span>
                            </div>
                        </div>

                        {/* Shipping Details */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.hazardousMaterial")}:</span>
                                <span className="text-primary ml-2">{container.isHazardous ? t("list.expandedDetails.hazardousYes", { hazClass: container.hazardClass || 'N/A' }) : t("list.expandedDetails.hazardousNo")}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.weightKg")}:</span>
                                <span className="text-primary ml-2">
                                    {container.totalWeightKg?.toLocaleString() || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.volumeCbm")}:</span>
                                <span className="text-primary ml-2">
                                    {container.totalVolumeCbm?.toLocaleString() || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.packages")}:</span>
                                <span className="text-primary ml-2">
                                    {container.totalPackages?.toLocaleString() || '-'}
                                </span>
                            </div>
                        </div>

                        {/* Financial */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.totalValue")}:</span>
                                <span className="text-primary ml-2">
                                    {container.totalValueUsd
                                        ? `$${container.totalValueUsd.toLocaleString()}`
                                        : container.value.amount > 0
                                            ? `${container.value.currency} ${container.value.amount.toLocaleString()}`
                                            : '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.freightCost")}:</span>
                                <span className="text-primary ml-2">
                                    {container.freightChargesUsd
                                        ? `$${container.freightChargesUsd.toLocaleString()}`
                                        : '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.insurance")}:</span>
                                <span className="text-primary ml-2">
                                    {container.insuranceValueUsd
                                        ? `$${container.insuranceValueUsd.toLocaleString()}`
                                        : '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.hsCode")}:</span>
                                <span className="text-primary ml-2">{container.hsCodePrimary || '-'}</span>
                            </div>
                        </div>

                        {/* Progress & Dates */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.progress")}:</span>
                                <span className="text-primary ml-2">{container.progress}%</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.bookingDate")}:</span>
                                <span className="text-primary ml-2">
                                    {container.bookingDate?.toLocaleDateString('it-IT') || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.departure")}:</span>
                                <span className="text-primary ml-2">
                                    {container.actualDepartureDate?.toLocaleDateString('it-IT')
                                        || container.estimatedDepartureDate?.toLocaleDateString('it-IT')
                                        || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.created")}:</span>
                                <span className="text-primary ml-2">
                                    {container.createdAt.toLocaleDateString('it-IT')}
                                </span>
                            </div>
                        </div>

                        {/* Tracking & Alerts */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.documentStatus")}:</span>
                                <span className={`ml-2 ${container.documentStatusOverall === 'All docs are OK' ? 'text-success-600' : 'text-warning-600'}`}>
                                    {container.documentStatusOverall || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.systemStatus")}:</span>
                                <span className={`ml-2 ${container.exogenStatusOverall === 'All systems OK' ? 'text-success-600' : 'text-warning-600'}`}>
                                    {container.exogenStatusOverall || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.trackingProvider")}:</span>
                                <span className="text-primary ml-2">{container.trackingProvider || '-'}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.lastTracked")}:</span>
                                <span className="text-primary ml-2">
                                    {container.lastTrackedAt?.toLocaleDateString('it-IT') || '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">{t("list.expandedDetails.activeAlerts")}:</span>
                                <span className="text-primary ml-2">
                                    {container.hasActiveAlerts ? t("list.expandedDetails.alertsYes", { count: container.alertCount || 0 }) : t("list.expandedDetails.alertsNo")}
                                </span>
                            </div>
                            {container.requiresTemperatureControl && (
                                <div>
                                    <span className="text-tertiary">{t("list.expandedDetails.temperature")}:</span>
                                    <span className="text-primary ml-2">
                                        {container.temperatureSetpointCelsius}°C
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

